import React, { useEffect, useMemo, useState } from 'react';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import FilterAltOffIcon from '@mui/icons-material/FilterAltOff';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import SearchIcon from '@mui/icons-material/Search';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Alert,
  Autocomplete,
  Box,
  Button,
  Chip,
  FormControl,
  FormControlLabel,
  FormHelperText,
  Grid,
  IconButton,
  InputLabel,
  MenuItem,
  Select,
  SelectChangeEvent,
  Stack,
  Switch,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import { IdNameDto } from '../../Shared/Types/common';
import {
  BookingSearchFilters,
  BookingSearchFormProps,
  defaultBookingSearchFilters,
} from '../Types/booking.types';
import { useGetBookingFilterSelectsQuery } from '../Service/extendedBookingsApi';

interface SummaryChip {
  key: string;
  label: string;
}

const areFiltersEqual = (
  currentFilters: BookingSearchFilters,
  nextFilters: BookingSearchFilters
): boolean =>
  (Object.keys(defaultBookingSearchFilters) as Array<keyof BookingSearchFilters>).every(
    (key) => currentFilters[key] === nextFilters[key]
  );

const toComparableDate = (value?: string): number | null => {
  if (!value) {
    return null;
  }

  const normalized = value.slice(0, 10);
  const timestamp = Date.parse(normalized);
  return Number.isNaN(timestamp) ? null : timestamp;
};

const findOptionLabel = (options: IdNameDto[] | undefined, value: string): string => {
  return options?.find((option) => String(option.id) === value)?.name ?? value;
};

const buildAdvancedFilterSummary = (
  filters: BookingSearchFilters,
  options: {
    offers?: IdNameDto[];
    companies?: IdNameDto[];
    publishedOptions?: IdNameDto[];
  }
): SummaryChip[] => {
  const chips: SummaryChip[] = [];

  if (filters.offerId) {
    chips.push({
      key: 'offerId',
      label: `Oferta: ${findOptionLabel(options.offers, filters.offerId)}`,
    });
  }

  if (filters.companyId) {
    chips.push({
      key: 'companyId',
      label: `Firma: ${findOptionLabel(options.companies, filters.companyId)}`,
    });
  }

  if (filters.applicant.trim()) {
    chips.push({
      key: 'applicant',
      label: `Zgłaszający: ${filters.applicant.trim()}`,
    });
  }

  if (filters.participant.trim()) {
    chips.push({
      key: 'participant',
      label: `Uczestnik: ${filters.participant.trim()}`,
    });
  }

  if (filters.applicantCity.trim()) {
    chips.push({
      key: 'applicantCity',
      label: `Miasto zgłaszającego: ${filters.applicantCity.trim()}`,
    });
  }

  if (filters.published) {
    chips.push({
      key: 'published',
      label: `Opublikowana: ${findOptionLabel(options.publishedOptions, filters.published)}`,
    });
  }

  if (filters.startDateFrom) {
    chips.push({
      key: 'startDateFrom',
      label: `Start terminu od: ${filters.startDateFrom}`,
    });
  }

  if (filters.startDateTo) {
    chips.push({
      key: 'startDateTo',
      label: `Start terminu do: ${filters.startDateTo}`,
    });
  }

  if (filters.numberOfParticipants) {
    chips.push({
      key: 'numberOfParticipants',
      label: `Uczestnicy: ${filters.numberOfParticipants}`,
    });
  }

  if (filters.dateFilterMode === 'overlapRange') {
    chips.push({
      key: 'dateFilterMode',
      label: 'Tryb dat: terminy nachodzące',
    });
  }

  if (filters.includeCancelled) {
    chips.push({
      key: 'includeCancelled',
      label: 'Pokaż anulowane',
    });
  }

  return chips;
};

const BookingSearchForm = ({
  onSearch,
  initialFilters,
}: BookingSearchFormProps & { initialFilters?: BookingSearchFilters }) => {
  const [filters, setFilters] = useState<BookingSearchFilters>(
    initialFilters ?? defaultBookingSearchFilters
  );
  const [validationError, setValidationError] = useState<string>('');
  const [advancedFiltersExpanded, setAdvancedFiltersExpanded] = useState<boolean>(false);
  const { data: filterSelects } = useGetBookingFilterSelectsQuery();

  useEffect(() => {
    const nextFilters = initialFilters ?? defaultBookingSearchFilters;
    setFilters((currentFilters) =>
      areFiltersEqual(currentFilters, nextFilters) ? currentFilters : nextFilters
    );
  }, [initialFilters]);

  const activeAdvancedFilters = useMemo(
    () => buildAdvancedFilterSummary(filters, filterSelects ?? {}),
    [filters, filterSelects]
  );

  const hasInvalidDateRange = (values: BookingSearchFilters): boolean => {
    if (!values.startDateFrom || !values.startDateTo) {
      return false;
    }

    const fromTimestamp = toComparableDate(values.startDateFrom);
    const toTimestamp = toComparableDate(values.startDateTo);

    if (fromTimestamp === null || toTimestamp === null) {
      return false;
    }

    return fromTimestamp > toTimestamp;
  };

  const hasInvalidParticipants = (values: BookingSearchFilters): boolean => {
    if (!values.numberOfParticipants) {
      return false;
    }

    const parsedParticipants = Number(values.numberOfParticipants);
    return !Number.isInteger(parsedParticipants) || parsedParticipants < 1;
  };

  const handleTextChange =
    (field: keyof BookingSearchFilters) =>
    (event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>): void => {
      const value = event.target.value;
      setFilters((current) => ({
        ...current,
        [field]: value,
      }));
    };

  const handleSelectChange =
    (field: keyof BookingSearchFilters) =>
    (event: SelectChangeEvent<string>): void => {
      setFilters((current) => ({
        ...current,
        [field]: event.target.value,
      }));
    };

  const handleSubmit = (event: React.FormEvent<HTMLFormElement>): void => {
    event.preventDefault();

    if (hasInvalidParticipants(filters)) {
      setValidationError(
        'Nieprawidłowa liczba uczestników: wartość musi być liczbą całkowitą większą lub równą 1.'
      );
      return;
    }

    if (hasInvalidDateRange(filters)) {
      setValidationError(
        "Nieprawidłowy zakres dat: 'Start terminu od' nie może być późniejsza niż 'Start terminu do'."
      );
      return;
    }

    setValidationError('');
    onSearch(filters);
  };

  const handleReset = (): void => {
    setValidationError('');
    setAdvancedFiltersExpanded(false);
    setFilters(defaultBookingSearchFilters);
    onSearch(defaultBookingSearchFilters);
  };

  return (
    <Box component="form" onSubmit={handleSubmit} sx={{ mt: 3 }}>
      <Grid container spacing={2}>
        <Grid size={{ xs: 12 }}>
          <TextField
            fullWidth
            label="Szukaj"
            name="searchTerm"
            placeholder="ID, oferta, firma, zgłaszający, uczestnik, miasto, data"
            value={filters.searchTerm}
            onChange={handleTextChange('searchTerm')}
          />
        </Grid>

        <Grid size={{ xs: 12 }}>
          <Accordion
            expanded={advancedFiltersExpanded}
            onChange={(_, expanded) => setAdvancedFiltersExpanded(expanded)}
            disableGutters
            elevation={0}
            sx={{
              border: (theme) => `1px solid ${theme.palette.divider}`,
              borderRadius: 1,
              '&:before': { display: 'none' },
            }}
          >
            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
              <Typography fontWeight={600}>Filtry zaawansowane</Typography>
            </AccordionSummary>
            <AccordionDetails>
              <Grid container spacing={2}>
                <Grid size={{ xs: 12, md: 6 }}>
                  <Autocomplete
                    options={filterSelects?.offers ?? []}
                    value={
                      (filterSelects?.offers ?? []).find(
                        (option) => String(option.id) === filters.offerId
                      ) ?? null
                    }
                    isOptionEqualToValue={(option, value) => option.id === value.id}
                    getOptionLabel={(option) => option.name ?? ''}
                    onChange={(_, selectedOption) => {
                      setFilters((current) => ({
                        ...current,
                        offerId:
                          selectedOption?.id !== undefined && selectedOption.id !== null
                            ? String(selectedOption.id)
                            : '',
                      }));
                    }}
                    renderInput={(params) => <TextField {...params} label="Oferta" fullWidth />}
                    clearOnEscape
                  />
                </Grid>

                <Grid size={{ xs: 12, md: 6 }}>
                  <FormControl fullWidth>
                    <InputLabel>Firma</InputLabel>
                    <Select
                      label="Firma"
                      value={filters.companyId}
                      onChange={handleSelectChange('companyId')}
                    >
                      <MenuItem value="">Wszystkie</MenuItem>
                      {(filterSelects?.companies ?? []).map((company) => (
                        <MenuItem key={String(company.id)} value={String(company.id)}>
                          {company.name}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>

                <Grid size={{ xs: 12, md: 4 }}>
                  <TextField
                    fullWidth
                    label="Zgłaszający"
                    value={filters.applicant}
                    onChange={handleTextChange('applicant')}
                  />
                </Grid>

                <Grid size={{ xs: 12, md: 4 }}>
                  <TextField
                    fullWidth
                    label="Uczestnik"
                    value={filters.participant}
                    onChange={handleTextChange('participant')}
                  />
                </Grid>

                <Grid size={{ xs: 12, md: 4 }}>
                  <TextField
                    fullWidth
                    label="Miasto zgłaszającego"
                    value={filters.applicantCity}
                    onChange={handleTextChange('applicantCity')}
                  />
                </Grid>

                <Grid size={{ xs: 12, md: 4 }}>
                  <FormControl fullWidth>
                    <InputLabel>Opublikowana</InputLabel>
                    <Select
                      label="Opublikowana"
                      value={filters.published}
                      onChange={handleSelectChange('published')}
                    >
                      <MenuItem value="">Wszystkie</MenuItem>
                      {(filterSelects?.publishedOptions ?? []).map((option) => (
                        <MenuItem key={String(option.id)} value={String(option.id)}>
                          {option.name}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>

                <Grid size={{ xs: 12, md: 4 }}>
                  <TextField
                    fullWidth
                    type="date"
                    label="Start terminu od"
                    InputLabelProps={{ shrink: true }}
                    value={filters.startDateFrom}
                    onChange={handleTextChange('startDateFrom')}
                  />
                </Grid>

                <Grid size={{ xs: 12, md: 4 }}>
                  <TextField
                    fullWidth
                    type="date"
                    label="Start terminu do"
                    InputLabelProps={{ shrink: true }}
                    value={filters.startDateTo}
                    onChange={handleTextChange('startDateTo')}
                  />
                </Grid>

                <Grid size={{ xs: 12, md: 6 }}>
                  <TextField
                    fullWidth
                    type="number"
                    label="Uczestnicy"
                    value={filters.numberOfParticipants}
                    onChange={handleTextChange('numberOfParticipants')}
                  />
                </Grid>

                <Grid size={{ xs: 12, md: 6 }}>
                  <FormControl component="fieldset" fullWidth>
                    <Box display="flex" alignItems="center" gap={1}>
                      <FormControlLabel
                        control={
                          <Switch
                            checked={filters.dateFilterMode === 'overlapRange'}
                            onChange={(event) => {
                              setFilters((current) => ({
                                ...current,
                                dateFilterMode: event.target.checked
                                  ? 'overlapRange'
                                  : 'startDate',
                              }));
                            }}
                          />
                        }
                        label="Uwzględnij terminy nachodzące na zakres"
                      />
                      <Tooltip title="Domyślnie daty filtrują po dacie rozpoczęcia terminu.">
                        <IconButton
                          size="small"
                          aria-label="Wyjaśnienie trybu filtrowania dat"
                          sx={{ p: 0.25 }}
                        >
                          <InfoOutlinedIcon color="action" fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    </Box>
                    <FormHelperText>
                      Domyślnie daty działają po starcie terminu. Włącz przełącznik, aby wyszukiwać
                      terminy nachodzące na zakres.
                    </FormHelperText>
                  </FormControl>
                </Grid>

                <Grid size={{ xs: 12 }}>
                  <FormControlLabel
                    control={
                      <Switch
                        checked={filters.includeCancelled === true}
                        onChange={(event) => {
                          const { checked } = event.target;
                          setFilters((current) => ({ ...current, includeCancelled: checked }));
                        }}
                      />
                    }
                    label="Pokaż anulowane"
                  />
                  <FormHelperText>
                    Domyślnie anulowane rezerwacje są ukryte. Włączenie pokaże aktywne i anulowane
                    razem.
                  </FormHelperText>
                </Grid>
              </Grid>
            </AccordionDetails>
          </Accordion>
        </Grid>

        {!advancedFiltersExpanded && activeAdvancedFilters.length > 0 && (
          <Grid size={{ xs: 12 }}>
            <Box
              sx={{
                border: (theme) => `1px solid ${theme.palette.divider}`,
                borderRadius: 1,
                p: 2,
              }}
            >
              <Typography variant="body2" color="text.secondary" gutterBottom>
                Aktywne filtry zaawansowane
              </Typography>
              <Stack direction="row" spacing={1} useFlexGap flexWrap="wrap">
                {activeAdvancedFilters.map((chip) => (
                  <Chip key={chip.key} label={chip.label} size="small" />
                ))}
              </Stack>
            </Box>
          </Grid>
        )}

        {validationError && (
          <Grid size={{ xs: 12 }}>
            <Alert severity="error">{validationError}</Alert>
          </Grid>
        )}

        <Grid size={{ xs: 12 }}>
          <Box display="flex" gap={2} flexWrap="wrap">
            <Button variant="contained" color="primary" type="submit" startIcon={<SearchIcon />}>
              Wyszukaj
            </Button>
            <Button
              variant="outlined"
              color="secondary"
              type="button"
              startIcon={<FilterAltOffIcon />}
              onClick={handleReset}
            >
              Wyczyść filtry
            </Button>
          </Box>
        </Grid>
      </Grid>
    </Box>
  );
};

export default BookingSearchForm;
